import { SharedUiColorNameV2 } from '@/shared/ui/theming';
import { ButtonStyle } from '@/ui/button/ui-button';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { switchMapFilter } from '@/utils/rxjs/operators/switch-map-filter.operator';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, filter, firstValueFrom, type Observable, of, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TailoredWidgetEditorModalComponent } from '../../components/tailored-widget-editor/tailored-widget-editor-modal/tailored-widget-editor-modal';
import type {
    TailoredWidgetEditorIntent,
    WidgetTemplateEditorCreationIntent
} from '../../components/tailored-widget-editor/tailored-widget-editor/tailored-widget-editor';
import { WidgetPickerModalComponent } from '../../components/widget-picker/widget-picker-modal/widget-picker-modal';
import { AdvizeoUiElementSize } from '../../interfaces/AdvizeoUi.interface';
import { BusinessIntelligenceDataSourceCode } from '../../interfaces/BusinessIntelligence.interface';
import { DashboardMode, DashboardScopeType } from '../../interfaces/Dashboard.interface';
import {
    type BusinessIntelligenceWidgetConfiguration,
    type EmptyTailoredWidget,
    type TailoredWidget,
    type TailoredWidgetConfig,
    type TailoredWidgetId,
    TailoredWidgetScopeType,
    type TailoredWidgetTemplate,
    TailoredWidgetTemplateCode,
    type TailoredWidgetTemplateId,
    type TailoredWidgetToCreate,
    type TailoredWidgetToEdit,
    type TailoredWidgetToSave,
    TailoredWidgetType,
    type TemplateWidgetConfig
} from '../../interfaces/TailoredWidget.interface';
import { AdvizeoModalService } from '../../services/advizeo-modal.service';
import type { AppState } from '../../store/app.state';
import { TailoredWidgetsStoreActions, TailoredWidgetsStoreSelectors } from '../../store/entities/tailored-widgets';
import { WidgetTemplateStoreActions, WidgetTemplateStoreSelectors } from '../../store/entities/widget-templates';
import { DashboardsProvider } from '../dashboards/dashboards.provider';
import { InformationProvider } from '../information/information.provider';
import { ProfileProvider } from '../profile/profile.provider';
import { StoreProvider } from '../store.provider';
import type { BusinessIntelligenceMeasureCode } from './../../interfaces/BusinessIntelligence.interface';
import { BusinessIntelligenceRepresentationsProvider } from './../business-intelligence/business-intelligence-representations.provider';
import { BusinessIntelligenceProvider } from './../business-intelligence/business-intelligence.provider';
import { AdvizeoCoreTailoredWidgetsProvider } from './advizeo-core-tailored-widgets.provider';
import type { TailoredWidgetsProvider } from './tailored-widgets.provider';

@Injectable({
    providedIn: 'root'
})
export class TailoredWidgetsProviderAdapter implements TailoredWidgetsProvider {
    private _advizeoCoreTailoredWidgetsProvider = inject(AdvizeoCoreTailoredWidgetsProvider);
    private _referenceListsService = inject(ReferenceListsService);
    private _store = inject<Store<AppState>>(Store);
    private _storeProvider = inject(StoreProvider);
    private _profileProvider = inject(ProfileProvider);
    private _advizeoModalService = inject(AdvizeoModalService);
    private _dashboardsProvider = inject(DashboardsProvider);
    private _translateService = inject(TranslateService);
    private _informationProvider = inject(InformationProvider);
    private _businessIntelligenceProvider = inject(BusinessIntelligenceProvider);
    private _businessIntelligenceRepresentationsProvider = inject(BusinessIntelligenceRepresentationsProvider);

    private _widgetDeleted$ = new Subject<TailoredWidgetId>();

    getWidget$(widgetId: TailoredWidgetId): Observable<TailoredWidget> {
        const widget$ = this._store.pipe(select(TailoredWidgetsStoreSelectors.selectTailoredWidgetById(widgetId)));
        return widget$.pipe(
            switchMap(widget => {
                if (
                    isNotNullish(widget) &&
                    isNotNullish(widget.configuration) &&
                    widget.configuration.widgetType === TailoredWidgetType.TEMPLATE_WIDGET
                ) {
                    return this._store.pipe(
                        select(
                            WidgetTemplateStoreSelectors.selectWidgetTemplateById(
                                widget.configuration.tailoredWidgetTemplateConfiguration!.widgetTemplateId
                            )
                        ),
                        map(template => {
                            return template
                                ? {
                                      ...widget,
                                      configuration: {
                                          ...widget.configuration,
                                          tailoredWidgetTemplateConfiguration: {
                                              ...(widget.configuration as TemplateWidgetConfig).tailoredWidgetTemplateConfiguration!,
                                              templateWidgetConfigurationExpression: template.configuration
                                          }
                                      }
                                  }
                                : widget;
                        })
                    );
                } else {
                    return of(widget!);
                }
            })
        );
    }

    getDuplicatedWidget$(widgetId: TailoredWidgetId): Observable<TailoredWidget> {
        return this.getWidget$(widgetId).pipe(map(widget => ({ ...widget, widgetId: null as unknown as TailoredWidgetId })));
    }

    getWidgetSize(widgetId: TailoredWidgetId): Promise<{ minWidth?: number; maxWidth?: number; minHeight?: number; maxHeight?: number }> {
        return firstValueFrom(
            this._store.pipe(select(TailoredWidgetsStoreSelectors.selectTailoredWidgetById(widgetId)), filter(isNotNullish))
        ).then(tailoredWidget => {
            return {
                minWidth: tailoredWidget.minWidth,
                maxWidth: tailoredWidget.maxWidth,
                minHeight: tailoredWidget.minHeight,
                maxHeight: tailoredWidget.maxHeight
            };
        });
    }

    getWidgetsSize$(widgetIds: TailoredWidgetId[]): Observable<
        {
            widgetId: TailoredWidgetId | null;
            minWidth?: number;
            maxWidth?: number;
            minHeight?: number;
            maxHeight?: number;
        }[]
    > {
        return combineLatest(
            widgetIds.map(widgetId => {
                return this.getWidget$(widgetId);
            })
        ).pipe(
            map(widgets => {
                return widgets.filter(isNotNullish).map(tailoredWidget => {
                    return {
                        widgetId: tailoredWidget.widgetId,
                        minWidth: tailoredWidget?.minWidth,
                        maxWidth: tailoredWidget?.maxWidth,
                        minHeight: tailoredWidget?.minHeight,
                        maxHeight: tailoredWidget?.maxHeight
                    };
                });
            })
        );
    }

    getEmptyWidget$(widgetTemplateId: TailoredWidgetTemplateId | null = null): Observable<EmptyTailoredWidget> {
        const emptyTailoredWidget: EmptyTailoredWidget = {
            isInCatalog: false,
            configuration: {
                widgetType: TailoredWidgetType.BUSINESS_INTELLIGENCE_WIDGET,
                businessIntelligenceConfiguration: null as unknown as BusinessIntelligenceWidgetConfiguration
            },
            canBeMonoSite: true,
            canBeMultiSite: true
        };

        if (widgetTemplateId) {
            return this._store.pipe(select(WidgetTemplateStoreSelectors.selectWidgetTemplateById(widgetTemplateId))).pipe(
                map(widgetTemplate => {
                    if (widgetTemplate) {
                        return {
                            isInCatalog: false,
                            canBeMonoSite: widgetTemplate.canBeMonoSite,
                            canBeMultiSite: widgetTemplate.canBeMultiSite,
                            configuration: {
                                widgetType: TailoredWidgetType.TEMPLATE_WIDGET,
                                tailoredWidgetTemplateConfiguration: {
                                    widgetTemplateId,
                                    templateWidgetConfiguration: null,
                                    templateWidgetConfigurationParams: null
                                },
                                templateWidgetConfigurationExpression: widgetTemplate.configuration
                            }
                        };
                    } else {
                        return emptyTailoredWidget;
                    }
                })
            );
        } else {
            return of(emptyTailoredWidget);
        }
    }

    isWidgetEditable$(widgetId: TailoredWidgetId, dashboardType: DashboardScopeType): Observable<boolean> {
        // TODO Change to backend computed "canbeUpdated" property when it will be available
        return combineLatest([this.getWidget$(widgetId), this._profileProvider.isProjectAdmin$()]).pipe(
            filter(([widget]) => isNotNullish(widget)),
            map(([widget, isProjectAdmin]) => {
                if (dashboardType === DashboardScopeType.USER && widget.type === TailoredWidgetScopeType.PROJECT) {
                    return false;
                }

                if (dashboardType === DashboardScopeType.USER && widget.type === TailoredWidgetScopeType.USER) {
                    return true;
                }

                return this.widgetCanBeChanged(widget, isProjectAdmin);
            })
        );
    }

    isWidgetDuplicable$(widgetId: TailoredWidgetId, dashboardType: DashboardScopeType): Observable<boolean> {
        return combineLatest([this.getWidget$(widgetId), this._profileProvider.isProjectAdmin$()]).pipe(
            filter(([widget]) => isNotNullish(widget)),
            map(([widget, isProjectAdmin]) => {
                if (
                    dashboardType === DashboardScopeType.USER &&
                    (widget.type === TailoredWidgetScopeType.USER || widget.type === TailoredWidgetScopeType.PROJECT)
                ) {
                    return true;
                }
                return this.widgetCanBeChanged(widget, isProjectAdmin);
            })
        );
    }

    isWidgetDeletable$(widgetId: TailoredWidgetId, dashboardType: DashboardScopeType): Observable<boolean> {
        return this.isWidgetEditable$(widgetId, dashboardType);
    }

    private widgetCanBeChanged(widget: TailoredWidget, isProjectAdmin: boolean): boolean {
        if (!(widget.type === TailoredWidgetScopeType.PROJECT && isProjectAdmin)) {
            return false;
        }

        const widgetConfig = widget.configuration;

        if (isNotNullish(widgetConfig) && widget.configuration.widgetType === TailoredWidgetType.BASIC_WIDGET) {
            return false;
        }
        if (isNotNullish(widgetConfig) && widgetConfig.widgetType === TailoredWidgetType.BUSINESS_INTELLIGENCE_WIDGET) {
            if (
                widgetConfig.businessIntelligenceConfiguration.dataSource &&
                widgetConfig.businessIntelligenceConfiguration.dataSource.dataSourceCode ===
                    BusinessIntelligenceDataSourceCode.DEET_EFA_ENERGY_CONSUMPTION
            ) {
                return false;
            }
        }

        if (isNotNullish(widgetConfig) && widget.configuration.widgetType === TailoredWidgetType.TEMPLATE_WIDGET) {
            if (widget.code === TailoredWidgetTemplateCode.CUSTOM_IMAGE || widget.code === TailoredWidgetTemplateCode.SECTION_TITLE) {
                return false;
            }
        }

        return true;
    }

    isWidgetCreatable(
        dashboardType: DashboardScopeType,
        numberOfWidgets: number,
        dashboardMode: DashboardMode
    ): { isCreatable: boolean; reason: string | null } {
        if (dashboardType === DashboardScopeType.USER) {
            const maxUserWidget = 15;

            const canCreateWidget = numberOfWidgets < maxUserWidget;

            return {
                isCreatable: canCreateWidget,
                reason: canCreateWidget
                    ? null
                    : this._translateService.instant(
                          dashboardMode === DashboardMode.MONO
                              ? 'DASHBOARD.MAX_BI_WIDGET_ON_MONO_DASHBOARD'
                              : 'DASHBOARD.MAX_BI_WIDGET_ON_MULTI_DASHBOARD'
                      )
            };
        }

        return {
            isCreatable: true,
            reason: null
        };
    }

    canBeReferencedInCatalog$(requiredDashboardScopeType: DashboardScopeType): Observable<boolean> {
        if (requiredDashboardScopeType === DashboardScopeType.USER) {
            return of(false);
        }
        return of(true);
    }

    fetchTailoredWidgets(): Promise<TailoredWidget[]> {
        this._store.dispatch(new TailoredWidgetsStoreActions.FetchTailoredWidgets());
        return firstValueFrom(this._advizeoCoreTailoredWidgetsProvider.fetchWidgets$())
            .then(tailoredWidgets => {
                this._store.dispatch(new TailoredWidgetsStoreActions.FetchTailoredWidgetsSucceeded({ tailoredWidgets }));
                return tailoredWidgets;
            })
            .catch(error => {
                this._store.dispatch(new TailoredWidgetsStoreActions.FetchTailoredWidgetsFailed({ error }));
                throw error;
            });
    }

    private getCatalogMonoSiteTailoredWidgetsList$(): Observable<TailoredWidget[]> {
        return this._store.pipe(select(TailoredWidgetsStoreSelectors.selectCatalogMonoSiteTailoredWidgets));
    }

    private getCatalogMultiSiteTailoredWidgetsList$(): Observable<TailoredWidget[]> {
        return this._store.pipe(select(TailoredWidgetsStoreSelectors.selectCatalogMultiSiteTailoredWidgets));
    }

    private getValidTailoredWidgetTemplateList(widgetsTemplate: TailoredWidgetTemplate[]): TailoredWidgetTemplate[] {
        return widgetsTemplate.filter(
            widget => widget.code !== TailoredWidgetTemplateCode.CUSTOM_IMAGE && widget.code !== TailoredWidgetTemplateCode.SECTION_TITLE
        );
    }

    private getCatalogMonoSiteTailoredWidgetTemplateList$(): Observable<TailoredWidgetTemplate[]> {
        this.initializeWidgetTemplatesStateIfNeeded();
        return this._store.pipe(select(WidgetTemplateStoreSelectors.selectMonoSiteWidgetTemplates)).pipe(
            map(widgetsTemplate => {
                return this.getValidTailoredWidgetTemplateList(widgetsTemplate);
            })
        );
    }

    private getCatalogMultiSiteTailoredWidgetTemplateList$(): Observable<TailoredWidgetTemplate[]> {
        this.initializeWidgetTemplatesStateIfNeeded();
        return this._store.pipe(select(WidgetTemplateStoreSelectors.selectMultiSiteWidgetTemplates)).pipe(
            map(widgetsTemplate => {
                return this.getValidTailoredWidgetTemplateList(widgetsTemplate);
            })
        );
    }

    getThumbnailUrl$(widgetId: TailoredWidgetId): Observable<string> {
        return this.getWidget$(widgetId).pipe(
            switchMap(widget => {
                if (!widget) {
                    return of(null);
                } else if (widget.thumbnailUrl) {
                    return of(widget.thumbnailUrl);
                } else if (
                    widget.configuration &&
                    widget.configuration.widgetType === TailoredWidgetType.BUSINESS_INTELLIGENCE_WIDGET &&
                    widget.configuration.businessIntelligenceConfiguration &&
                    widget.configuration.businessIntelligenceConfiguration.representation
                ) {
                    const representationTypeItem$ = this._referenceListsService.getReferenceListItem$(
                        ReferenceListName.WIDGET_REPRESENTATION_TYPE,
                        widget.configuration.businessIntelligenceConfiguration.representation.representationCode
                    );

                    return representationTypeItem$.pipe(map(item => (item ? item.iconUrl : null)));
                } else {
                    return of(null);
                }
            }),
            filter(isNotNullish)
        );
    }

    getTemplateThumbnailUrl$(widgetTemplateId: TailoredWidgetTemplateId): Observable<string> {
        return this.getTailoredWidgetTemplateById$(widgetTemplateId).pipe(map(template => template.thumbnailUrl));
    }

    requestWidgetEdition(
        widgetId: TailoredWidgetId,
        targetDashboardMode: DashboardMode,
        targetDashboardScopeType: DashboardScopeType
    ): Promise<void> {
        const actionIntent: TailoredWidgetEditorIntent = {
            editionIntent: {
                widgetId
            },
            requiredDashboardModes: [targetDashboardMode],
            requiredDashboardScopeType: targetDashboardScopeType
        };

        return this.openWidgetEditor(actionIntent).then(() => {
            return;
        });
    }

    requestWidgetDuplication(
        widgetId: TailoredWidgetId,
        targetDashboardMode: DashboardMode,
        targetDashboardScopeType: DashboardScopeType
    ): Promise<TailoredWidgetId> {
        const actionIntent: TailoredWidgetEditorIntent = {
            duplicationIntent: {
                widgetId
            },
            requiredDashboardModes: [targetDashboardMode],
            requiredDashboardScopeType: targetDashboardScopeType
        };

        return this.openWidgetEditor(actionIntent).then(widgetId => {
            return widgetId as TailoredWidgetId;
        });
    }

    requestBusinessIntelligenceWidgetCreation(
        targetDashboardMode: DashboardMode,
        targetDashboardScopeType: DashboardScopeType
    ): Promise<TailoredWidgetId> {
        const actionIntent: TailoredWidgetEditorIntent = {
            creationIntent: {
                widgetType: TailoredWidgetType.BUSINESS_INTELLIGENCE_WIDGET
            },
            requiredDashboardModes: [targetDashboardMode],
            requiredDashboardScopeType: targetDashboardScopeType
        };

        return this.openWidgetEditor(actionIntent).then(widgetId => {
            return widgetId as TailoredWidgetId;
        });
    }

    requestWidgetCreationFromTemplate(
        widgetTemplateId: TailoredWidgetTemplateId,
        targetDashboardMode: DashboardMode,
        targetDashboardScopeType: DashboardScopeType
    ): Promise<TailoredWidgetId> {
        const creationIntent: WidgetTemplateEditorCreationIntent = {
            widgetType: TailoredWidgetType.TEMPLATE_WIDGET,
            widgetTemplateId
        };
        const actionIntent: TailoredWidgetEditorIntent = {
            creationIntent,
            requiredDashboardModes: [targetDashboardMode],
            requiredDashboardScopeType: targetDashboardScopeType
        };
        return this.openWidgetEditor(actionIntent).then(widgetId => {
            return widgetId as TailoredWidgetId;
        });
    }

    private openWidgetEditor(actionIntent: TailoredWidgetEditorIntent): Promise<TailoredWidgetId | void> {
        return this._advizeoModalService
            .openAdvizeoModal(TailoredWidgetEditorModalComponent, actionIntent, {
                size: AdvizeoUiElementSize.HUGE,
                noPadding: true
            })
            .afterClosed();
    }

    pickWidgetFromCatalog(dashboardMode: DashboardMode, dashboardScopeType: DashboardScopeType): Promise<TailoredWidgetId | void> {
        const widgetPickerModalInput = {
            tailoredWidgetTemplateList$: (dashboardMode === DashboardMode.MONO
                ? this.getCatalogMonoSiteTailoredWidgetTemplateList$()
                : this.getCatalogMultiSiteTailoredWidgetTemplateList$()
            ).pipe(switchMapFilter(widget => this.isTemplateWidgetAccessibleForDashboardScopeType$(widget.code, dashboardScopeType))),
            tailoredWidgetList$: (dashboardMode === DashboardMode.MONO
                ? this.getCatalogMonoSiteTailoredWidgetsList$()
                : this.getCatalogMultiSiteTailoredWidgetsList$()
            ).pipe(switchMapFilter(widget => this.isWidgetAccessibleForDashboardScopeType(widget.configuration, dashboardScopeType))),
            targetDashboardMode: dashboardMode,
            targetDashboardScopeType: dashboardScopeType
        };
        return this._advizeoModalService
            .openAdvizeoModal(WidgetPickerModalComponent, widgetPickerModalInput, { size: AdvizeoUiElementSize.LARGE })
            .afterClosed();
    }

    private isTemplateWidgetAccessibleForDashboardScopeType$(
        templateWidgetTemplateCode: TailoredWidgetTemplateCode,
        dashboardScopeType: DashboardScopeType
    ): Observable<boolean> {
        if (
            dashboardScopeType === DashboardScopeType.USER &&
            templateWidgetTemplateCode === TailoredWidgetTemplateCode.CUSTOM_IMAGE_BASE64
        ) {
            return of(false);
        }
        return of(true);
    }

    private isWidgetAccessibleForDashboardScopeType(
        widgetConfiguration: TailoredWidgetConfig,
        dashboardScopeType: DashboardScopeType
    ): Observable<boolean> {
        switch (widgetConfiguration.widgetType) {
            case TailoredWidgetType.BUSINESS_INTELLIGENCE_WIDGET:
                const dataSourceCode = widgetConfiguration.businessIntelligenceConfiguration.dataSource.dataSourceCode;
                return this._businessIntelligenceProvider.isDataSourceAccessibleForDashboardScopeType$(dataSourceCode, dashboardScopeType);
            default:
                return of(true);
        }
    }

    saveWidget(widgetToSave: TailoredWidgetToSave): Promise<TailoredWidgetId | void> {
        this.displayInformationWhenSavingWidget(widgetToSave);
        if (widgetToSave && widgetToSave.widgetId) {
            return this.editWidget(widgetToSave.widgetId, {
                name: widgetToSave.name,
                isInCatalog: widgetToSave.isInCatalog,
                canBeMonoSite: widgetToSave.canBeMonoSite,
                canBeMultiSite: widgetToSave.canBeMultiSite,
                configuration: widgetToSave.configuration,
                widgetTemplateId: widgetToSave.widgetTemplateId,
                widgetTemplateParams: widgetToSave.widgetTemplateParams
            });
        } else {
            return this.createWidget({
                name: widgetToSave.name,
                type: widgetToSave.type,
                isInCatalog: widgetToSave.isInCatalog,
                canBeMonoSite: widgetToSave.canBeMonoSite,
                canBeMultiSite: widgetToSave.canBeMultiSite,
                configuration: widgetToSave.configuration,
                widgetTemplateId: widgetToSave.widgetTemplateId,
                widgetTemplateParams: widgetToSave.widgetTemplateParams
            });
        }
    }

    private displayInformationWhenSavingWidget(widgetToSave: TailoredWidgetToSave) {
        let message: string | null = null;
        if (isNotNullish(widgetToSave.configuration)) {
            const measures: BusinessIntelligenceMeasureCode[] = this._businessIntelligenceRepresentationsProvider
                .getMeasureCodesFromRepresentationConfig(
                    widgetToSave.configuration.representation.representationCode,
                    widgetToSave.configuration.representation.representationConfig
                )
                .map(measure => measure.measureCode);

            message = this._businessIntelligenceProvider.getDataSourceConfigurationInformationMessage({
                dataSourceCode: widgetToSave.configuration.dataSource.dataSourceCode,
                measures
            });
        }

        if (isNotNullish(message)) {
            this._advizeoModalService.openDialog({
                title: this._translateService.instant('STUFF.INFORMATION'),
                message,
                buttons: [
                    {
                        text: this._translateService.instant('STUFF.OK'),
                        color: SharedUiColorNameV2.PRIMARY,
                        onClick: () => {}
                    }
                ]
            });
        }
    }

    async requestWidgetDeletion(widgetId: TailoredWidgetId) {
        const dashboardIds = await firstValueFrom(this._advizeoCoreTailoredWidgetsProvider.fetchDashboardsByWidgetId$(widgetId));
        const dashboardNames = await firstValueFrom(this._dashboardsProvider.getDashboardNames$(dashboardIds));

        this._advizeoModalService.openDialog({
            title: this._translateService.instant('TAILORED_WIDGET.DELETION_MODAL.TITLE'),
            message: dashboardIds.length
                ? this._translateService.instant('TAILORED_WIDGET.DELETION_MODAL.WARNING_MESSAGE', {
                      dashboardNames: dashboardNames.join(', ')
                  })
                : this._translateService.instant('TAILORED_WIDGET.DELETION_MODAL.UNUSED_WIDGET_MESSAGE'),
            buttons: [
                {
                    text: this._translateService.instant('BUTTONS.DELETE'),
                    onClick: () => {
                        this.deleteWidget(widgetId);
                    },
                    color: dashboardIds.length ? SharedUiColorNameV2.DANGER : SharedUiColorNameV2.PRIMARY
                },
                {
                    text: this._translateService.instant('BUTTONS.CANCEL'),
                    onClick: 'cancel',
                    color: SharedUiColorNameV2.PRIMARY,
                    buttonStyle: ButtonStyle.OUTLINE
                }
            ]
        });
    }

    widgetDeleted$(): Observable<TailoredWidgetId> {
        return this._widgetDeleted$.asObservable();
    }

    createWidget(widgetToCreate: TailoredWidgetToCreate): Promise<TailoredWidgetId> {
        return firstValueFrom(this._advizeoCoreTailoredWidgetsProvider.createWidget$(widgetToCreate)).then(widgetId => {
            this.fetchTailoredWidgets();
            return widgetId;
        });
    }

    private editWidget(widgetId: TailoredWidgetId, widgetToEdit: TailoredWidgetToEdit): Promise<void> {
        return firstValueFrom(this._advizeoCoreTailoredWidgetsProvider.editWidget$(widgetId, widgetToEdit)).then(() => {
            this.fetchTailoredWidgets();
        });
    }

    private deleteWidget(widgetId: TailoredWidgetId) {
        return firstValueFrom(this._advizeoCoreTailoredWidgetsProvider.deleteWidget$(widgetId))
            .then(() => {
                this.fetchTailoredWidgets();
                this._widgetDeleted$.next(widgetId);
                this._informationProvider.showToast(this._translateService.instant('TAILORED_WIDGET.DELETION.SUCCESS_MESSAGE'), 'success');
            })
            .catch(err => {
                console.error('error while deleting widget', err);
                this._informationProvider.showToast(this._translateService.instant('TAILORED_WIDGET.DELETION.ERROR_MESSAGE'), 'error');
            });
    }

    getTailoredWidgetTemplateById$(widgetTemplateId: TailoredWidgetTemplateId): Observable<TailoredWidgetTemplate> {
        this.initializeWidgetTemplatesStateIfNeeded();
        return this._store.pipe(select(WidgetTemplateStoreSelectors.selectWidgetTemplateById(widgetTemplateId)), filter(isNotNullish));
    }

    private initializeWidgetTemplatesStateIfNeeded() {
        const pending = this._storeProvider.getStoreInstantValue(WidgetTemplateStoreSelectors.selectIsPending);
        const loaded = this._storeProvider.getStoreInstantValue(WidgetTemplateStoreSelectors.selectIsLoaded);

        if (!pending && !loaded) {
            this.initializeWidgetTemplatesState();
        }
    }

    private initializeWidgetTemplatesState() {
        this.fetchWidgetTemplates();
    }

    private fetchWidgetTemplates(): Promise<TailoredWidgetTemplate[]> {
        this._store.dispatch(new WidgetTemplateStoreActions.FetchWidgetTemplates());
        return firstValueFrom(this._advizeoCoreTailoredWidgetsProvider.fetchWidgetTemplates$())
            .then(widgetTemplates => {
                this._store.dispatch(new WidgetTemplateStoreActions.FetchWidgetTemplatesSucceeded({ widgetTemplates }));
                return widgetTemplates;
            })
            .catch(error => {
                this._store.dispatch(new WidgetTemplateStoreActions.FetchWidgetTemplatesFailed(error));
                throw error;
            });
    }

    getWidgetCatalogRepresentationType(widget: TailoredWidget): string | null {
        if (!widget) {
            return null;
        } else if (
            widget.configuration &&
            widget.configuration.widgetType === TailoredWidgetType.BUSINESS_INTELLIGENCE_WIDGET &&
            widget.configuration.businessIntelligenceConfiguration &&
            widget.configuration.businessIntelligenceConfiguration.representation
        ) {
            return widget.configuration.businessIntelligenceConfiguration.representation.representationCode;
        } else {
            return '';
        }
    }

    getWidgetCatalogDataSource(widget: TailoredWidget): BusinessIntelligenceDataSourceCode | null {
        if (!widget) {
            return null;
        } else if (
            widget.configuration &&
            widget.configuration.widgetType === TailoredWidgetType.BUSINESS_INTELLIGENCE_WIDGET &&
            widget.configuration.businessIntelligenceConfiguration &&
            widget.configuration.businessIntelligenceConfiguration.dataSource
        ) {
            return widget.configuration.businessIntelligenceConfiguration.dataSource.dataSourceCode;
        } else {
            return null;
        }
    }

    areWidgetsLoading$(): Observable<boolean> {
        return this._store.pipe(select(TailoredWidgetsStoreSelectors.selectIsPending));
    }
}
